import { produce } from "immer";
import { hfInstance } from "../../Cells";
import { ProcessTypes, RowTypes } from "../../../../../api.app/controllers/constants";
import { rangeToHfFormat } from "../CellsHelper";
import Merge from "./Merge";
import { arrayMoveImmutable } from "../../../utils/array";
const utils = (context) => {
  const { current } = context;
  return {
    refineRangeForAddition(range, activeCellIndex) {
      const isGrid = current.type === ProcessTypes.Grid;
      if (isGrid) {
        return this._shrinkRangeByRowType(range, activeCellIndex, RowTypes.Grid);
      } else {
        let type;
        for (let i = range[1]; i <= range[3]; i++) {
          if (!type && [RowTypes.RowGroup, RowTypes.Activity].includes(current.design.rows[i].type)) {
            type = current.design.rows[i].type;
            break;
          }
        }
        return this._shrinkRangeByRowType(range, activeCellIndex, type ?? RowTypes.RowGroup);
      }
    },
    _shrinkRangeByRowType(range, activeCellIndex, type) {
      let start, end;
      for (let i = range[1]; i <= range[3]; i++) {
        if (!start && current.design.rows[i].type === type) start = i;
        if (current.design.rows[i].type === type) end = i;
      }
      if (!start) {
        start = end = current.design.rows.findIndex((item) => item.type === type);
        activeCellIndex[1] = start;
      }

      return [[range[0], start, range[2], end], activeCellIndex, type];
    },
  };
};

const main = (context) => {
  const { dispatch, currentSheet, currentSheetId, currentSheetName, current } = context;
  return {
    addRowsToState(index, count, type, newRowIds) {
      var newRows = [];
      for (let i = 0; i < count; i++) newRows.push({ id: newRowIds[i], type });
      current.design.rows = produce(current.design.rows, (rows) => {
        rows.splice(index, 0, ...newRows);
      });
      dispatch("cells/calculateRowMap", null, { root: true });
      dispatch("cells/addRowColumnMap", { index, rows: newRows }, { root: true });

      // Undo Operation
      return async () => {
        const rowIds = newRows.map((item) => item.id);
        current.design.rows = produce(current.design.rows, (rows) => {
          rows.splice(index, count);
        });
        dispatch("cells/calculateRowMap", null, { root: true });
        dispatch("cells/removeRowColumnMap", { rowIds }, { root: true });
      };
    },
    addRowsToHf(index, count) {
      hfInstance.addRows(currentSheetId, [index, count]);
      return () => {
        hfInstance.removeRows(currentSheetId, [index, count]);
      };
    },
    async removeRowsFromState(index, count) {
      const rowIndexes = new Array(count).fill(0).map((item, i) => index + i);
      Merge(context).utils.recalculateMergedCells({ rowIndexes });

      const prevRows = current.design.rows.slice(index, index + count);
      const prevRowIds = prevRows.map((row) => row.id);
      current.design.rows = produce(current.design.rows, (rows) => {
        rows.splice(index, count);
      });
      dispatch("cells/calculateRowMap", null, { root: true });
      await dispatch("cells/removeRowColumnMap", { rowIds: prevRowIds }, { root: true });

      await dispatch("cells/removeNodesFromCellsContent", { rowIds: prevRowIds }, { root: true });

      return async () => {
        current.design.rows = produce(current.design.rows, (rows) => {
          rows.splice(index, 0, ...prevRows);
        });
        dispatch("cells/calculateRowMap", null, { root: true });
        dispatch("cells/addRowColumnMap", { index, rows: prevRows }, { root: true });
        Merge(context).utils.recalculateMergedCells({ rowIndexes });
      };
    },
    async removeRowsFromHf(range) {
      const count = range[3] - range[1] + 1;
      const index = range[1];
      const prevCellContents = hfInstance.getRangeSerialized(rangeToHfFormat(currentSheet, range));
      hfInstance.removeRows(currentSheetId, [index, count]);

      return async () => {
        hfInstance.addRows(currentSheetId, [index, count]);
        hfInstance.setCellContents({ sheet: currentSheetId, row: range[1], col: range[0] }, prevCellContents);

        await dispatch("cells/persistSelectedCells", { range, value: prevCellContents }, { root: true });
      };
    },
    async moveRowsFromState(source, target, count) {
      const rowIdsToMove = new Set(current.design.rows.slice(source, source + count).map((row) => row.id));
      const targetRowIds = new Set(current.design.rows.slice(target, target + count).map((row) => row.id));
      const allRowIds = new Set([...rowIdsToMove, ...targetRowIds]);
      current.design.rows = arrayMoveImmutable(current.design.rows, source, target, count);
      await dispatch("cells/calculateRowMap", null, { root: true });

      const updatedRows = [];
      for (const id of allRowIds) {
        if (current.design.rowMap[id]) {
          updatedRows.push(current.design.rowMap[id]);
        }
      }
      await dispatch("cells/persistMoveRows", { updatedRows }, { root: true });

      return async () => {
        current.design.rows = arrayMoveImmutable(current.design.rows, target, source, count);
        dispatch("cells/calculateRowMap", null, { root: true });
        const updatedRows = [];
        for (const id of allRowIds) {
          if (current.design.rowMap[id]) {
            updatedRows.push(current.design.rowMap[id]);
          }
        }
        await dispatch("cells/persistMoveRows", { updatedRows }, { root: true });
      };
    },
    async moveRowsFromHf(source, target, count) {
      // in hyperformula target is the row before which a row is added
      hfInstance.moveRows(currentSheetId, source, count, target > source ? target + count : target);
      return () => {
        this.moveRowsFromHf(target, source, count);
      };
    },
  };
};

export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
